import { useEffect, useState } from 'react'
import i18n from '../../../i18n'

const ACCESS_KEY = 'access_token'
const REFRESH_KEY = 'refresh_token'

/** Émis après setTokens / clearTokens pour resynchroniser l’UI (même onglet). */
export const AUTH_TOKENS_CHANGED_EVENT = 'auth:tokens-changed'

/** Message utilisé quand le token est invalide/expiré (401) ; aussi comparé tel quel pour reconnaître l’erreur. */
export function sessionExpiredMessage(): string {
  return i18n.t('auth:tokenStorage.session_expiree')
}

function notifyAuthTokensChanged(): void {
  if (typeof window === 'undefined') return
  try {
    window.dispatchEvent(new Event(AUTH_TOKENS_CHANGED_EVENT))
  } catch {
    /* ignore */
  }
}

export function getAccessToken(): string | null {
  try {
    return localStorage.getItem(ACCESS_KEY)
  } catch {
    return null
  }
}

export function getRefreshToken(): string | null {
  try {
    return localStorage.getItem(REFRESH_KEY)
  } catch {
    return null
  }
}

export function setAccessToken(access: string): void {
  localStorage.setItem(ACCESS_KEY, access)
  notifyAuthTokensChanged()
}

export function setTokens(tokens: { access: string; refresh: string }): void {
  localStorage.setItem(ACCESS_KEY, tokens.access)
  localStorage.setItem(REFRESH_KEY, tokens.refresh)
  notifyAuthTokensChanged()
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY)
  localStorage.removeItem(REFRESH_KEY)
  notifyAuthTokensChanged()
}

export function isAuthenticated(): boolean {
  return Boolean(getAccessToken())
}

/**
 * À appeler quand une requête renvoie 401. Efface les tokens pour que l’UI affiche « non connecté ».
 */
export function clearTokensIfUnauthorized(): void {
  clearTokens()
}

/**
 * Auth réactive : se met à jour après clearTokens/setTokens (même onglet) et `storage` (autre onglet).
 * Évite de continuer à appeler /me/* après un 401 qui a vidé le token.
 */
export function useIsAuthenticated(): boolean {
  const [authed, setAuthed] = useState(() => isAuthenticated())
  useEffect(() => {
    const sync = () => setAuthed(isAuthenticated())
    window.addEventListener(AUTH_TOKENS_CHANGED_EVENT, sync)
    window.addEventListener('storage', sync)
    // Les effets des enfants passent avant ceux du parent : un setTokens posé au montage
    // (retour OAuth) part avant notre abonnement. On relit donc l'état une fois abonné.
    sync()
    return () => {
      window.removeEventListener(AUTH_TOKENS_CHANGED_EVENT, sync)
      window.removeEventListener('storage', sync)
    }
  }, [])
  return authed
}

